import React, { useEffect, useState, useRef } from 'react';
import { courseAPI, masterAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiPrinter, FiDownload, FiUsers } from 'react-icons/fi';
import { useReactToPrint } from 'react-to-print';
import StudentIDCard from '../components/StudentIDCard';

const BulkIDCards = () => {
  const [classes, setClasses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [instituteName, setInstituteName] = useState('Your Institute Name');
  
  const [filters, setFilters] = useState({
    class_id: '',
    batch_id: ''
  });

  const componentRef = useRef();

  useEffect(() => {
    fetchClasses();
    fetchBatches();
    fetchInstituteName();
  }, []);

  const fetchClasses = async () => {
    try {
      const response = await masterAPI.getClasses();
      if (response.data.success) {
        setClasses(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const fetchBatches = async () => {
    try {
      const response = await masterAPI.getBatches();
      if (response.data.success) {
        setBatches(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };

  const fetchInstituteName = async () => {
    try {
      // Try to get institute name from local storage or use default
      const storedName = localStorage.getItem('instituteName');
      if (storedName) {
        setInstituteName(storedName);
      } else {
        // Use a default name - user can edit this
        setInstituteName('Your Institute Name');
      }
    } catch (error) {
      console.error('Error fetching institute name:', error);
      setInstituteName('Your Institute Name');
    }
  };

  const fetchStudents = async () => {
    if (!filters.class_id && !filters.batch_id) {
      toast.error('Please select at least class or batch');
      return;
    }

    setLoading(true);
    try {
      const params = {
        class_id: filters.class_id,
        batch_id: filters.batch_id
      };
      
      const response = await courseAPI.getStudents(params);
      if (response.data.success) {
        setStudents(response.data.data);
        if (response.data.data.length === 0) {
          toast.info('No students found for selected filters');
        } else {
          toast.success(`Loaded ${response.data.data.length} student(s)`);
        }
      }
    } catch (error) {
      toast.error('Error fetching students');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `Student_ID_Cards_${new Date().toISOString().split('T')[0]}`,
    onBeforeGetContent: () => {
      console.log('Preparing to print...');
      return Promise.resolve();
    },
    onAfterPrint: () => {
      toast.success('Print dialog opened successfully!');
    },
    onPrintError: (error) => {
      console.error('Print error:', error);
      toast.error('Error opening print dialog. Try the manual print option.');
    },
    pageStyle: `
      @page {
        size: A4;
        margin: 10mm;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .id-card-container {
          page-break-after: always;
          page-break-inside: avoid;
        }
      }
    `
  });

  const handleManualPrint = async () => {
    try {
      toast.info('Preparing cards for print...');
      
      // Convert all canvas elements (QR codes) to images
      const canvases = componentRef.current.querySelectorAll('canvas');
      const canvasData = [];
      
      canvases.forEach(canvas => {
        try {
          const dataUrl = canvas.toDataURL('image/png');
          canvasData.push({
            canvas: canvas,
            dataUrl: dataUrl
          });
        } catch (err) {
          console.error('Error converting canvas:', err);
        }
      });
      
      // Replace canvases with images temporarily
      canvasData.forEach(item => {
        const img = document.createElement('img');
        img.src = item.dataUrl;
        img.style.width = item.canvas.width + 'px';
        img.style.height = item.canvas.height + 'px';
        img.className = 'qr-code-image';
        item.canvas.style.display = 'none';
        item.canvas.parentNode.appendChild(img);
      });
      
      // Get the HTML content
      const printContent = componentRef.current.innerHTML;
      
      // Restore canvases
      canvasData.forEach(item => {
        item.canvas.style.display = '';
        const img = item.canvas.parentNode.querySelector('.qr-code-image');
        if (img) {
          img.remove();
        }
      });
      
      // Open print window
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error('Please allow popups for this site to print');
        return;
      }

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Student ID Cards - ${new Date().toISOString().split('T')[0]}</title>
            <style>
              @page {
                size: A4;
                margin: 10mm;
              }
              body {
                margin: 0;
                padding: 0;
                font-family: Arial, sans-serif;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              .id-card-container {
                page-break-after: always;
                page-break-inside: avoid;
              }
              .id-card {
                width: 350px;
                min-height: 600px;
                margin: 20px auto;
                border: 2px solid #e5e7eb;
                font-family: Arial, sans-serif;
                background: white;
                border-radius: 0.5rem;
                box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
                overflow: visible;
              }
              .card-header {
                background: linear-gradient(to right, #2563eb, #1e40af);
                color: white;
                padding: 1rem;
                text-align: center;
              }
              .card-body {
                padding: 1.5rem;
              }
              .card-footer {
                background: #f3f4f6;
                padding: 0.75rem;
                text-align: center;
              }
              @media print {
                body {
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }
                .id-card-container {
                  page-break-after: always;
                }
              }
            </style>
            <link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet">
          </head>
          <body>
            ${printContent}
          </body>
        </html>
      `);
      printWindow.document.close();
      
      // Wait for images to load, then print
      setTimeout(() => {
        printWindow.print();
        toast.success('Manual print dialog opened!');
      }, 1000);
      
    } catch (error) {
      console.error('Manual print error:', error);
      toast.error('Error preparing print: ' + error.message);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  return (
    <>
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #print-area, #print-area * {
            visibility: visible;
          }
          #print-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .id-card-container {
            page-break-after: always;
            page-break-inside: avoid;
          }
          @page {
            size: A4;
            margin: 10mm;
          }
        }
      `}</style>
      <div className="p-4 md:p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Generate ID Cards</h1>
          <p className="text-sm md:text-base text-gray-600 mt-1">Create student ID cards with QR codes for attendance</p>
        </div>
      </div>

      {/* Institute Name */}
      <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Institute Name</h3>
        <div className="flex gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Name to appear on ID cards
            </label>
            <input
              type="text"
              value={instituteName}
              onChange={(e) => {
                setInstituteName(e.target.value);
                localStorage.setItem('instituteName', e.target.value);
              }}
              placeholder="Enter your institute name"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
            />
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          This name will appear on all generated ID cards
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Students</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Class
            </label>
            <select
              name="class_id"
              value={filters.class_id}
              onChange={handleFilterChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
            >
              <option value="">All Classes</option>
              {classes.map(cls => (
                <option key={cls.id} value={cls.id}>{cls.class}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Batch/Section
            </label>
            <select
              name="batch_id"
              value={filters.batch_id}
              onChange={handleFilterChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
            >
              <option value="">All Batches</option>
              {batches.map(batch => (
                <option key={batch.id} value={batch.id}>{batch.batch}</option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={fetchStudents}
              disabled={loading}
              className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              {loading ? 'Loading...' : 'Load Students'}
            </button>
          </div>
        </div>
      </div>

      {/* Actions */}
      {students.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center flex-wrap">
            <button
              onClick={handlePrint}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <FiPrinter /> Print All Cards ({students.length})
            </button>
            <button
              onClick={handleManualPrint}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FiPrinter /> Manual Print (with QR)
            </button>
            <button
              onClick={() => {
                window.print();
                toast.info('Use browser print (Ctrl+P). Make sure to enable "Background graphics"');
              }}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <FiPrinter /> Simple Print (Ctrl+P)
            </button>
            <div className="text-sm text-gray-600 flex items-center">
              <FiUsers className="mr-2" />
              {students.length} student(s) selected
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
            <p className="text-xs text-blue-900 font-semibold mb-1">💡 Print Tips:</p>
            <ul className="text-xs text-blue-800 space-y-1">
              <li>• <strong>Recommended:</strong> Try "Manual Print (with QR)" - QR codes included</li>
              <li>• <strong>Alternative:</strong> Use "Simple Print" then Ctrl+P (or Cmd+P on Mac)</li>
              <li>• <strong>Important:</strong> Enable "Background graphics" in print settings</li>
              <li>• <strong>Best Quality:</strong> Save as PDF first, then print the PDF</li>
            </ul>
          </div>
        </div>
      )}

      {/* Preview */}
      {students.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview (First 3 cards)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {students.slice(0, 3).map(student => (
              <div key={student.id} className="transform scale-75 origin-top">
                <StudentIDCard student={student} instituteName={instituteName} />
              </div>
            ))}
          </div>
          {students.length > 3 && (
            <p className="text-center text-gray-600 mt-4">
              + {students.length - 3} more cards (will be included in print)
            </p>
          )}
        </div>
      )}

      {/* Hidden Print Area */}
      <div id="print-area" style={{ display: 'none' }}>
        <div ref={componentRef}>
          {students.map(student => (
            <StudentIDCard key={student.id} student={student} instituteName={instituteName} />
          ))}
        </div>
      </div>

      {/* Empty State */}
      {!loading && students.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <FiUsers size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Students Loaded</h3>
          <p className="text-gray-600">Select a class or batch and click "Load Students" to generate ID cards</p>
        </div>
      )}
      </div>
    </>
  );
};

export default BulkIDCards;
